// Board filter in the URL (Plan nav-filters): who (people, "me", "none" = unassigned; any of them), project, which
// closed statuses to show (open only by default) and text:
//   ?who=me,usr_x,none  &project=spc_x  &show=<closed status names>  &q=words
// Only the URL and its chips here; what the filter keeps, the statuses and the counts are the core's (board.view).
import type { BoardQuery, Snapshot } from "@/api";

export const ME = "me";
export const NONE = "none";

export type BoardFilter = {
	/** person ids, ME, NONE; empty = everyone */
	who: string[];
	project: string | null;
	/** closed statuses to show (their names) */
	show: string[];
	q: string;
};
export const EMPTY: BoardFilter = { who: [], project: null, show: [], q: "" };

const list = (v: string | null) => (v ? v.split(",").filter(Boolean) : []);

export function parseFilter(p: URLSearchParams): BoardFilter {
	const who = list(p.get("who"));
	// before nav-filters: ?person=<id|none>
	const old = p.get("person");
	if (old && !who.includes(old)) who.push(old);
	return { who, project: p.get("project") || null, show: list(p.get("show")), q: p.get("q") ?? "" };
}

/** `f` into `p` (other params such as ?id= stay). */
export function writeFilter(p: URLSearchParams, f: BoardFilter): URLSearchParams {
	const q = new URLSearchParams(p);
	for (const k of ["who", "project", "show", "q", "person"]) q.delete(k);
	if (f.who.length) q.set("who", f.who.join(","));
	if (f.project) q.set("project", f.project);
	if (f.show.length) q.set("show", f.show.join(","));
	if (f.q.trim()) q.set("q", f.q);
	return q;
}

/** The filter as the core's board view takes it. */
export const queryOf = (f: BoardFilter, hits: ReadonlySet<string> | null): BoardQuery => ({ ...f, hits: hits ? [...hits] : null });

export const isEmpty = (f: BoardFilter) => !f.who.length && !f.project && !f.show.length && !f.q.trim();
export const toggle = <T>(xs: T[], x: T) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);

/** Initials: the first letters of the first two words, one letter for a one-word name; a prefix such as
 *  "agent:" is left out ("agent:tasks-web" → TW). */
export function initials(name: string): string {
	const w = (name.split(":").at(-1) ?? "").trim().split(/[\s._-]+/).filter(Boolean);
	const s = w.length > 1 ? w[0]![0]! + w[1]![0]! : (w[0]?.[0] ?? "?");
	return s.toUpperCase();
}

export type Chip = { key: string; label: string; remove: (f: BoardFilter) => BoardFilter };

/** Active filters as removable chips, in a stable order. */
export function chipsOf(f: BoardFilter, s: Snapshot | null, labels: { me: string; none: string }): Chip[] {
	const name = (id: string) => (id === ME ? labels.me : id === NONE ? labels.none : (s?.persons.find((p) => p.id === id)?.name ?? id));
	const out: Chip[] = f.who.map((w) => ({ key: `who:${w}`, label: name(w), remove: (x) => ({ ...x, who: x.who.filter((y) => y !== w) }) }));
	// the project is the scope (the nav's picker shows it), not a filter chip
	for (const t of f.show) out.push({ key: `show:${t}`, label: t, remove: (x) => ({ ...x, show: x.show.filter((y) => y !== t) }) });
	if (f.q.trim()) out.push({ key: "q", label: `\u201c${f.q.trim()}\u201d`, remove: (x) => ({ ...x, q: "" }) });
	return out;
}
